import { useState, type ReactNode } from 'react';
import { FiCreditCard, FiLayers } from 'react-icons/fi';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { formatBDT } from '@maktech/shared';
import { errorMessage } from '@/lib/api/client';
import { createOrder } from '@/api/commerce.api';
import type { Quote } from '@/api/commerce.api';
import { Modal } from '@/components/common/Modal';
import { useCart } from '@/features/commerce/CartContext';
import { clearAffiliateRef, getAffiliateRef } from '@/features/affiliate/referralAttribution';
import { clearAppliedCoupon } from '@/features/affiliate/pendingCoupon';
import { applyDemoAffiliatePurchase } from '@/pages/admin/demo/store/adminDemoStore';

/** Payment methods — spec 7.4. */
/**
 * SPEC/PROTOTYPE CONFLICT — REPORTED, spec followed. See the note to the client.
 *
 * The prototype's checkout offers FOUR methods; its wiring map (:1876) is
 * literally {"💳 Card":"card","bKash":"bkash","Nagad":"nagad","PayPal":"paypal"}
 * — there is no Rocket chip. Spec 7.4 says "bKash / Nagad / Rocket / card /
 * PayPal", five. The rule is that the prototype wins, which would mean dropping
 * Rocket; the reason it is kept is that the prototype CONTRADICTS ITSELF rather
 * than disagreeing with the spec: its own trust badge (:2197) lists Rocket, and
 * its own payment-help page (:4065) says "bKash, Nagad, Rocket, card and PayPal
 * — step by step". Shipping the 4-chip version would advertise a wallet at
 * checkout that the checkout refuses to take. Labels/order follow the prototype.
 */
const METHODS: { id: 'bkash' | 'nagad' | 'rocket' | 'card' | 'paypal'; label: string; icon?: ReactNode }[] = [
  { id: 'card', label: 'Card', icon: <FiCreditCard className="size-4" aria-hidden /> },
  { id: 'bkash', label: 'bKash' },
  { id: 'nagad', label: 'Nagad' },
  { id: 'rocket', label: 'Rocket' },
  { id: 'paypal', label: 'PayPal' },
];

/**
 * Checkout — completePurchase() (:2268 base + :5234 v13 wrapper).
 *
 * No payment provider is wired yet (SSLCommerz is a Phase 10 integration), so
 * this records a paid order directly, exactly as the prototype did. The
 * difference is that the SERVER decides the amount: we post slugs + coupon and
 * the API re-quotes. Whatever this screen displays is cosmetic; the charge is
 * computed again server-side.
 */
export function CheckoutModal({
  quote,
  coupon,
  onClose,
}: {
  quote: Quote;
  coupon: string | null;
  onClose: () => void;
}) {
  const [method, setMethod] = useState<(typeof METHODS)[number]['id']>('card');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { clearCourses } = useCart();
  const navigate = useNavigate();
  const qc = useQueryClient();

  async function pay() {
    setBusy(true);
    setError(null);
    try {
      const affiliateCode = getAffiliateRef() ?? undefined;
      const r = await createOrder({
        courseSlugs: quote.items.map((i) => i.slug),
        couponCode: coupon ?? undefined,
        method,
        affiliateCode,
      });
      if (affiliateCode) {
        applyDemoAffiliatePurchase(
          affiliateCode,
          r.total ?? quote.total,
          quote.items.map((i) => ({ slug: i.slug, title: i.title, price: i.price })),
          coupon,
        );
        clearAffiliateRef();
      }
      clearAppliedCoupon();
      clearCourses();
      await qc.invalidateQueries({ queryKey: ['course'] });
      await qc.invalidateQueries({ queryKey: ['enrollments'] });
      onClose();
      const first = r.items[0]?.slug;
      // completePurchase (:2288): one course opens the player, several go to
      // the dashboard. The dashboard is Phase 5, so多-course lands on the
      // first course's player until it exists.
      if (first) navigate(`/learn/${first}/0/0`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} label="Checkout">
      <h3 className="mb-2.5">Checkout</h3>

      <div id="checkoutSummary">
        {quote.items.map((i) => (
          <div className="flex justify-between py-[7px] text-[14.5px]" key={i.slug}>
            <span>{i.title}</span>
            <span>{formatBDT(i.price)}</span>
          </div>
        ))}
        {quote.bundles.map((b) => (
          <div className="flex justify-between py-[7px] text-[14.5px] text-[var(--teal)]" key={b.slug}>
            <span className="inline-flex items-center gap-1.5">
              <FiLayers className="size-4 shrink-0" aria-hidden />
              {b.title} (−{b.discountPct}%)
            </span>
            <span>−{formatBDT(b.discount)}</span>
          </div>
        ))}
        {quote.couponDiscount > 0 ? (
          <div className="flex justify-between py-[7px] text-[14.5px] text-[var(--teal)]">
            <span>
              Coupon {quote.couponCode} (−{quote.couponPct}%)
            </span>
            <span>−{formatBDT(quote.couponDiscount)}</span>
          </div>
        ) : null}
        <div className="mt-1 flex justify-between border-t border-[var(--line)] py-[7px] text-[17px] font-extrabold">
          <span>Total</span>
          <span data-testid="checkout-total">{formatBDT(quote.total)}</span>
        </div>
      </div>

      {/* Checkout modal (:602) — "Pay with" is a .chips row and the selected
          method is .chip.active (selectPay :1321 toggles exactly that class).
          These were btn-primary/btn-ghost buttons in a .pay-methods div, which
          is not a class the prototype has and not how the row looks. */}
      <div className="mt-[18px] mb-1.5 text-sm font-bold">Pay with</div>
      <div className="mb-4 flex flex-wrap gap-2.5" id="payChips">
        {METHODS.map((m) => (
          <button
            key={m.id}
            className={`rounded-full border-[1.5px] px-[18px] py-[9px] text-sm font-semibold transition ${
              method === m.id
                ? 'border-transparent bg-linear-to-br from-[var(--ember)] to-[var(--cobalt)] text-white shadow-[var(--shadow-orange)]'
                : 'border-[var(--line)] bg-[var(--card)] text-[var(--ink-soft)] hover:border-[var(--cobalt)] hover:text-[var(--cobalt)]'
            }`}
            data-testid={`pay-${m.id}`}
            onClick={() => setMethod(m.id)}
          >
            {m.icon ? (
              <span className="inline-flex items-center gap-1.5">
                {m.icon}
                {m.label}
              </span>
            ) : (
              m.label
            )}
          </button>
        ))}
      </div>

      {error ? (
        <p data-testid="checkout-error" className="mt-2.5 text-[13.5px] text-[var(--coral)]">
          {error}
        </p>
      ) : null}

      <button
        className="mt-3.5 inline-flex w-full items-center justify-center gap-2 rounded-full border-0 bg-linear-to-br from-[var(--ember)] to-[var(--cobalt)] px-[34px] py-4 text-base font-semibold text-white shadow-[var(--shadow-orange)] hover:-translate-y-0.5 disabled:opacity-60"
        data-testid="checkout-pay"
        disabled={busy}
        onClick={() => void pay()}
      >
        {busy ? 'Processing…' : `Pay ${formatBDT(quote.total)}`}
      </button>
      <p className="mt-2 text-center text-[11.5px] text-[var(--ink-soft)]">
        No payment provider is connected yet — this records the order without taking money.
      </p>
    </Modal>
  );
}
